import { API_URL } from '../config/api_url';

export async function fetchJson(path, options = {}) {
  const token = localStorage.getItem('access_token');

  const headers = new Headers(options.headers || {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }

  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : null;
}

export function getJwtPayload(token) {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function normalizeRole(role) {
  return String(role || '').replace(/^ROLE_/, '').toUpperCase();
}

export function getUserRoles(token = localStorage.getItem('access_token')) {
  const payload = getJwtPayload(token);
  if (!payload) return [];

  let roles = [];

  if (Array.isArray(payload.roles)) {
    roles = payload.roles;
  } else if (Array.isArray(payload.authorities)) {
    roles = payload.authorities;
  } else if (payload.role) {
    roles = [payload.role];
  }

  return roles.map(normalizeRole);
}

export function hasRole(role, token = localStorage.getItem('access_token')) {
  const roles = getUserRoles(token);
  return roles.includes(normalizeRole(role));
}

export function hasAnyRole(allowedRoles = [], token = localStorage.getItem('access_token')) {
  const roles = getUserRoles(token);
  const normalizedAllowed = allowedRoles.map(normalizeRole);
  return roles.some((role) => normalizedAllowed.includes(role));
}

// Admin login uses SAME endpoint as user login
export async function apiLogin(username, password) {
  const response = await fetch(`${API_URL}/user/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}`);
  }

  const data = await response.json().catch(() => null);

  if (!data?.token) {
    throw new Error('Invalid response from server');
  }

  localStorage.setItem('access_token', data.token);

  const tokenRoles = getUserRoles(data.token);
  const allowedRoles = ['ADMIN', 'MODERATOR', 'AUTHOR'];

  if (tokenRoles.length && !tokenRoles.some((r) => allowedRoles.includes(r))) {
    localStorage.removeItem('access_token');
    throw new Error('This account is not allowed');
  }

  return data;
}

export function logout() {
  localStorage.removeItem('access_token');
}

export function isJwtExpired(token, skewMs = 10_000) {
  const payload = getJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return Date.now() >= payload.exp * 1000 - skewMs;
}

export function clearAuthStorage() {
  localStorage.clear();
  sessionStorage.clear();
}